import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Search } from "@air/next-icons";
import { useBreakpointsContext } from "@air/provider-media-query";
import { tailwindMerge } from "@air/tailwind-variants";
import { ComboboxInput } from "@reach/combobox";
import classNames from "classnames";
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { SearchRightButtons } from "~/components/SearchBar/SearchRightButtons";
import { SEARCH_INPUT, SEARCH_INPUT_CONTAINER } from "~/constants/testIDs";
import { useFocusable } from "~/hooks/useFocusable";
const HORIZONTAL_PADDING = 8;
const CHILDREN_PADDING = 8;
const SEARCH_ICON_SIZE = 24;
const BASIC_LEFT_PADDING = SEARCH_ICON_SIZE + 2 * HORIZONTAL_PADDING;
const focusedSearchInputContainerClasses = "absolute md:relative bg-grey-1 z-1 md:z-[initial] top-0 left-0 right-0 md:top-[initial] md:left-[initial] md:right-[initial] h-14";
const _SearchInput = /*#__PURE__*/ forwardRef((param, forwardedRef)=>{
    let { value, onChange, hasSuggestions, onFocusChange, onEnterPress, placeholder, onBackspacePress, onClearClick, onCloseClick, shortcutsInfo, onSearchActivated, children } = param;
    const [isFocused, setIsFocused] = useState(false);
    const [isRightButtonFocused, setIsRightButtonFocused] = useState(false);
    const [rightPadding, setRightPadding] = useState(HORIZONTAL_PADDING);
    const [leftPadding, setLeftPadding] = useState(BASIC_LEFT_PADDING);
    const { isAboveMediumScreen } = useBreakpointsContext();
    const isActive = !!value;
    const setFocused = useCallback(()=>{
        setIsFocused(true);
        onFocusChange(true);
    }, [
        onFocusChange
    ]);
    const setBlurred = useCallback(()=>{
        setIsFocused(false);
        onFocusChange(false);
    }, [
        onFocusChange
    ]);
    const [onFocus, onBlur] = useFocusable(setFocused, setBlurred);
    const rightButtonsRef = useRef(null);
    const childrenContainerRef = useRef(null);
    const inputRef = useRef(null);
    const isRightButtonsVisible = isFocused || isRightButtonFocused || !!value;
    const isClearBtnVisible = (isRightButtonFocused || isFocused) && !!value;
    useEffect(()=>{
        /**
       * This code observes children size changes and sets correct left padding,
       * so that placeholder is moved to left when children are present.
       */ const element = childrenContainerRef.current;
        const observer = new ResizeObserver((entries)=>{
            const rect = entries[0].contentRect;
            if (children && childrenContainerRef.current && rect.width > 0) {
                setLeftPadding(BASIC_LEFT_PADDING + rect.width + CHILDREN_PADDING);
            } else {
                setLeftPadding(BASIC_LEFT_PADDING);
            }
        });
        if (element) {
            observer.observe(element);
        }
        return ()=>{
            element && observer.unobserve(element);
        };
    }, [
        children
    ]);
    useImperativeHandle(forwardedRef, ()=>({
            blur: ()=>{
                var _inputRef_current;
                inputRef === null || inputRef === void 0 ? void 0 : (_inputRef_current = inputRef.current) === null || _inputRef_current === void 0 ? void 0 : _inputRef_current.blur();
            },
            focus: ()=>{
                var _inputRef_current;
                inputRef === null || inputRef === void 0 ? void 0 : (_inputRef_current = inputRef.current) === null || _inputRef_current === void 0 ? void 0 : _inputRef_current.focus();
            }
        }));
    useEffect(()=>{
        if (!isAboveMediumScreen) {
            var _inputRef_current;
            inputRef === null || inputRef === void 0 ? void 0 : (_inputRef_current = inputRef.current) === null || _inputRef_current === void 0 ? void 0 : _inputRef_current.focus();
        }
    }, [
        isAboveMediumScreen
    ]);
    useEffect(()=>{
        if (isRightButtonsVisible && rightButtonsRef.current) {
            const paddingWithClearBtn = rightButtonsRef.current.clientWidth + HORIZONTAL_PADDING;
            setRightPadding(paddingWithClearBtn);
            if (value && !isClearBtnVisible) {
                // 60 is clear btn width with vertical separator
                setRightPadding(paddingWithClearBtn - 60);
            }
        } else {
            setRightPadding(HORIZONTAL_PADDING);
        }
    }, [
        rightButtonsRef,
        isRightButtonsVisible,
        isClearBtnVisible,
        value
    ]);
    useEffect(()=>{
        if (!value && isFocused) {
            // when user clears value but its still focus, we want to show suggestions
            // combobox does not support it, so simulate a click to trigger suggestion popover
            setTimeout(()=>{
                var _inputRef_current;
                (_inputRef_current = inputRef.current) === null || _inputRef_current === void 0 ? void 0 : _inputRef_current.click();
            });
        }
    // call this effect only when value changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        value
    ]);
    const onKeyDown = useCallback((event)=>{
        switch(event.key){
            case "Enter":
                {
                    var _event_target, _inputRef_current;
                    event.preventDefault();
                    event.stopPropagation();
                    onEnterPress((_event_target = event.target) === null || _event_target === void 0 ? void 0 : _event_target.value);
                    (_inputRef_current = inputRef.current) === null || _inputRef_current === void 0 ? void 0 : _inputRef_current.blur();
                    break;
                }
            case "Backspace":
                {
                    const target = event.target;
                    const selectionStart = target.selectionStart;
                    if (selectionStart === 0) {
                        onBackspacePress === null || onBackspacePress === void 0 ? void 0 : onBackspacePress();
                    }
                    break;
                }
            case "Escape":
                {
                    var _inputRef_current1;
                    event.preventDefault();
                    (_inputRef_current1 = inputRef.current) === null || _inputRef_current1 === void 0 ? void 0 : _inputRef_current1.blur();
                    break;
                }
        }
    }, [
        onBackspacePress,
        onEnterPress
    ]);
    const focusedSearchInputClasses = tailwindMerge("border-b-solid h-14 rounded-none border-b border-none border-b-grey-5 py-0 placeholder-grey-8 shadow-none md:h-[initial] md:rounded-t-lg md:border-[initial] md:py-4 md:shadow-[0_2px_8px_rgba(0,0,0,0.2)]", hasSuggestions ? "" : "md:rounded-b-lg");
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": SEARCH_INPUT_CONTAINER,
        className: "".concat(tailwindMerge("group/search-input-container relative flex h-10 items-center rounded md:-mt-1 md:h-12", isFocused ? focusedSearchInputContainerClasses : "")),
        children: [
            /*#__PURE__*/ _jsx(Search, {
                onClick: ()=>{
                    var _inputRef_current;
                    (_inputRef_current = inputRef.current) === null || _inputRef_current === void 0 ? void 0 : _inputRef_current.focus();
                    onSearchActivated();
                },
                className: "".concat(tailwindMerge("absolute text-grey-9 md:top-3 [&>path]:stroke-[2.5]", isFocused ? "top-3.5" : "top-2")),
                style: {
                    left: HORIZONTAL_PADDING,
                    width: SEARCH_ICON_SIZE,
                    height: SEARCH_ICON_SIZE
                }
            }),
            !!children && /*#__PURE__*/ _jsx("div", {
                ref: childrenContainerRef,
                className: "absolute",
                style: {
                    left: SEARCH_ICON_SIZE + HORIZONTAL_PADDING + CHILDREN_PADDING
                },
                children: children
            }),
            /*#__PURE__*/ _jsx(ComboboxInput, {
                onClick: onSearchActivated,
                "data-testid": SEARCH_INPUT,
                onKeyDown: onKeyDown,
                onFocus: onFocus,
                onBlur: onBlur,
                ref: inputRef,
                placeholder: placeholder,
                value: value,
                onChange: onChange,
                className: tailwindMerge("h-10 w-full rounded border py-2.5 text-16 text-grey-12 placeholder-grey-9 outline-none transition md:h-[initial] group-hover/search-input-container:[&:not(:focus)]:shadow-[0_0_0_3px] group-hover/search-input-container:[&:not(:focus)]:shadow-grey-4", isFocused ? focusedSearchInputClasses : "", isActive ? "border-grey-7" : "border-grey-5"),
                style: {
                    paddingLeft: leftPadding,
                    paddingRight: rightPadding,
                    transition: "padding 250ms, border-top-right-radius 250ms, border-top-left-radius 250ms"
                }
            }),
            /*#__PURE__*/ _jsx("div", {
                ref: rightButtonsRef,
                className: classNames("absolute flex items-center md:top-[initial]", isFocused ? "top-4" : "top-2"),
                style: {
                    right: HORIZONTAL_PADDING,
                    opacity: isRightButtonsVisible ? 1 : 0,
                    pointerEvents: isRightButtonsVisible ? "all" : "none"
                },
                children: /*#__PURE__*/ _jsx(SearchRightButtons, {
                    onFocusChange: setIsRightButtonFocused,
                    hasValue: !!value,
                    isClearBtnVisible: isClearBtnVisible,
                    onClearClick: onClearClick,
                    onCloseClick: onCloseClick
                })
            }),
            shortcutsInfo && !isRightButtonsVisible && !value ? /*#__PURE__*/ _jsx("div", {
                className: "shortcutsInfo pointer-events-none absolute hidden md:group-hover/search-input-container:flex",
                style: {
                    right: HORIZONTAL_PADDING
                },
                id: "#shortcutsInfo",
                children: shortcutsInfo
            }) : null
        ]
    });
});
_SearchInput.displayName = "_SearchInput";
export const SearchInput = /*#__PURE__*/ memo(_SearchInput);
SearchInput.displayName = "SearchInput";
